import React from "react";
import {
  Baby,
  Sparkles,
  Briefcase,
  Sunset,
  Crown,
} from "lucide-react";

const STAGE_ICONS = {
  childhood: Baby,
  "young-adult": Sparkles,
  "mid-life": Briefcase,
  "pre-retirement": Sunset,
  retirement: Crown,
};

const FALLBACK_CONCERN = {
  childhood: "College planning",
  "young-adult": "Family protection",
  "mid-life": "Wealth accumulation",
  "pre-retirement": "Retirement readiness",
  retirement: "Legacy & care",
};

export const VisionArc = ({ stages = [] }) => {
  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative py-20 sm:py-28 bg-lw-ivory-warm"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 reveal">
            <span className="lw-eyebrow">Our Vision</span>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
              Financial strength,{" "}
              <span className="italic">passed forward.</span>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-lw-muted max-w-md">
              <p>
                We believe wealth isn&rsquo;t a destination &mdash; it&rsquo;s
                a posture you hold across decades. The right protection at 30
                funds the right growth at 45, which funds the right income at
                65, which funds the legacy you leave at 85.
              </p>
              <p>
                Our advisors work across all five chapters of life, so the
                decisions you make today still serve the family you&rsquo;ll
                be tomorrow.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <ArcVisual stages={stages} />
          </div>
        </div>
      </div>
    </section>
  );
};

const ArcVisual = ({ stages }) => {
  const safeStages = stages.length
    ? stages
    : [
        { id: "childhood", name: "Childhood", age_range: "0–18" },
        { id: "young-adult", name: "Young Adult", age_range: "18–40" },
        { id: "mid-life", name: "Mid-Life", age_range: "40–55" },
        { id: "pre-retirement", name: "Pre-Retirement", age_range: "55–65" },
        { id: "retirement", name: "Retirement", age_range: "65–85" },
      ];

  return (
    <div
      data-testid="lifespan-arc"
      className="relative bg-white rounded-2xl border border-lw-navy/8 p-6 sm:p-10 shadow-[0_30px_80px_-40px_rgba(11,37,69,0.25)]"
    >
      {/* Arc SVG */}
      <div className="relative w-full" style={{ aspectRatio: "5 / 1.4" }}>
        <svg
          viewBox="0 0 1000 240"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="arc-gradient" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#D4A24C" stopOpacity="0.25" />
              <stop offset="50%" stopColor="#D4A24C" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#D4A24C" stopOpacity="0.25" />
            </linearGradient>
          </defs>
          <path
            d="M 40 200 C 200 30, 800 30, 960 200"
            stroke="url(#arc-gradient)"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
        </svg>

        {/* Stage dots positioned along the arc (approx) */}
        {safeStages.slice(0, 5).map((stage, i) => {
          // approximate y positions on the arc cubic curve
          const positions = [
            { x: "4%", y: "78%" },
            { x: "25%", y: "30%" },
            { x: "50%", y: "16%" },
            { x: "75%", y: "30%" },
            { x: "96%", y: "78%" },
          ];
          const Icon = STAGE_ICONS[stage.id] || Sparkles;
          const pos = positions[i];
          return (
            <div
              key={stage.id}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: pos.x, top: pos.y }}
              aria-hidden="true"
            >
              <div className="arc-stage-dot w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-lw-navy text-[#FAF8F3] flex items-center justify-center border-2 border-lw-gold/60 shadow-md">
                <Icon size={18} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="gold-hairline mt-2 mb-6" />

      {/* Stage labels grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 sm:gap-3">
        {safeStages.slice(0, 5).map((stage) => {
          const Icon = STAGE_ICONS[stage.id] || Sparkles;
          const concern =
            (stage.primary_concerns && stage.primary_concerns[0]) ||
            FALLBACK_CONCERN[stage.id] ||
            "";
          return (
            <div
              key={stage.id}
              data-testid={`life-stage-${stage.id}`}
              className="arc-stage flex flex-col items-start sm:items-center sm:text-center"
            >
              <div className="sm:hidden mb-2 text-lw-gold">
                <Icon size={18} />
              </div>
              <div className="font-serif-display text-lg text-lw-navy leading-tight">
                {stage.name}
              </div>
              <div className="font-sans-ui text-[0.7rem] tracking-[0.18em] uppercase text-lw-gold mt-1">
                Ages {stage.age_range}
              </div>
              <div className="mt-2 text-sm text-lw-muted leading-snug">
                {concern}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VisionArc;
